import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { num, formatApiError } from "@/lib/api";
import { Card, PageHeader, Btn, Modal, Input, Select, StatusBadge, EmptyState, Th, Td } from "@/components/admin/ui";

export default function Workers() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [locs, setLocs] = useState({ countries: [], regions: [], cities: [], teams: [] });
  const [form, setForm] = useState({ name: "", email: "", password: "worker123", phone: "", country_id: "", region_id: "", city_id: "", team_id: "", timezone: "Asia/Kolkata", role: "worker" });

  const load = useCallback(() => {
    api.get(`/workers?q=${encodeURIComponent(q)}&page=${page}&limit=25`).then((r) => setData(r.data)).catch(() => {});
  }, [q, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/countries").then((r) => setLocs((s) => ({ ...s, countries: r.data.items })));
    api.get("/cities").then((r) => setLocs((s) => ({ ...s, cities: r.data.items })));
    api.get("/teams").then((r) => setLocs((s) => ({ ...s, teams: r.data.items })));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null));
      payload.name = form.name; payload.email = form.email; payload.password = form.password; payload.role = form.role; payload.timezone = form.timezone;
      const { data: w } = await api.post("/workers", payload);
      toast.success(`Worker created · ${w.worker_id}`);
      setOpen(false); load();
      setForm({ name: "", email: "", password: "worker123", phone: "", country_id: "", region_id: "", city_id: "", team_id: "", timezone: "Asia/Kolkata", role: "worker" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const suspend = async (wid) => {
    try { await api.post(`/workers/${wid}/suspend`); toast.success("Worker suspended"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const pages = Math.ceil(data.total / 25) || 1;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResults(null);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const rows = lines.slice(1).map((line) => {
        const cells = line.split(",");
        const obj = {};
        headers.forEach((h, i) => (obj[h] = (cells[i] || "").trim()));
        return obj;
      });
      const { data: res } = await api.post("/workers/bulk-import", { rows });
      setImportResults(res);
      toast.success(`${res.created} of ${res.total} workers imported`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Workers" subtitle={`${num(data.total)} workers in your scope`}
        action={<div className="flex gap-2">
          <Btn variant="ghost" data-testid="import-workers-btn" onClick={() => { setImportOpen(true); setImportResults(null); }}>Import CSV</Btn>
          <Btn data-testid="add-worker-btn" onClick={() => setOpen(true)}>+ Add Worker</Btn>
        </div>} />
      <div className="flex gap-3">
        <input data-testid="workers-search" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search by name, email, ID, phone…"
          className="flex-1 px-4 py-2 rounded-lg bg-[#151D2A] border border-white/10 text-white text-sm focus:border-[#1D4ED8] outline-none" />
      </div>
      <Card>
        {data.items.length === 0 ? <EmptyState title="No workers found" desc="Add your first worker to start assigning tasks and tracking performance." action={<Btn onClick={() => setOpen(true)}>+ Add Worker</Btn>} /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-white/10"><tr><Th>Worker ID</Th><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Timezone</Th><Th>Status</Th><Th></Th></tr></thead>
              <tbody>
                {data.items.map((w) => (
                  <tr key={w.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`worker-row-${w.worker_id}`}>
                    <Td className="font-mono-metric text-[#1D4ED8]">{w.worker_id}</Td>
                    <Td className="font-medium text-white">{w.name}</Td>
                    <Td className="text-slate-400">{w.email}</Td>
                    <Td>{w.role.replace(/_/g, " ")}</Td>
                    <Td className="text-slate-400">{w.timezone}</Td>
                    <Td><StatusBadge status={w.status} /></Td>
                    <Td>{w.status !== "suspended" && <button onClick={() => suspend(w.worker_id)} data-testid={`suspend-${w.worker_id}`} className="text-xs text-rose-400 hover:underline">Suspend</button>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Btn variant="ghost" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>Prev</Btn>
          <span className="text-sm text-slate-400">Page {page} / {pages}</span>
          <Btn variant="ghost" onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages}>Next</Btn>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add Worker">
        <form onSubmit={create} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Full name" data-testid="worker-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input label="Email" type="email" data-testid="worker-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Password" data-testid="worker-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Country" value={form.country_id} onChange={(e) => setForm({ ...form, country_id: e.target.value })}>
              <option value="">—</option>{locs.countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Select label="City" data-testid="worker-city-select" value={form.city_id} onChange={(e) => { const city = locs.cities.find((x) => x.id === e.target.value); setForm({ ...form, city_id: e.target.value, region_id: city?.region_id || "", country_id: city?.country_id || form.country_id }); }}>
              <option value="">—</option>{locs.cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Team" data-testid="worker-team-select" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })}>
              <option value="">—</option>{locs.teams.filter((t) => !form.city_id || t.city_id === form.city_id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
            <Select label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="worker">Worker</option><option value="team_leader">Team Leader</option>
            </Select>
          </div>
          <Input label="Timezone (IANA)" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
          <Btn data-testid="worker-submit" className="w-full">Create Worker</Btn>
        </form>
      </Modal>

      <Modal open={importOpen} onClose={() => setImportOpen(false)} title="Bulk Import Workers" wide>
        <div className="space-y-4">
          <p className="text-sm text-slate-400">Upload a CSV with a header row. Supported columns: <span className="font-mono-metric text-slate-200">name, email, password, phone, city, team, role, timezone</span>. City and team are matched by name; unknown values are left unassigned.</p>
          <a href="data:text/csv;charset=utf-8,name,email,password,phone,city,team,role,timezone%0AJane%20Doe,jane@example.com,worker123,+911234567890,Chennai,Team Alpha,worker,Asia/Kolkata"
            download="gtforce_worker_template.csv" className="inline-block text-sm text-[#1D4ED8] font-semibold" data-testid="csv-template-link">↓ Download CSV template</a>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-slate-400">Choose CSV file</span>
            <input type="file" accept=".csv,text/csv" onChange={handleFile} data-testid="csv-file-input" disabled={importing}
              className="mt-2 block w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#1D4ED8] file:text-white file:font-semibold" />
          </label>
          {importing && <p className="text-slate-400 text-sm">Importing…</p>}
          {importResults && (
            <div>
              <div className="text-sm text-emerald-400 mb-2">{importResults.created} created · {importResults.total - importResults.created} skipped</div>
              <div className="max-h-64 overflow-y-auto cc-scroll space-y-1">
                {importResults.results.map((r, i) => (
                  <div key={i} className={`flex justify-between px-3 py-1.5 rounded text-xs ${r.status === "created" ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"}`} data-testid={`import-result-${r.line}`}>
                    <span>Row {r.line}: {r.email || "—"}</span>
                    <span>{r.status === "created" ? r.worker_id : r.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
